/**
 * PauseAndReflectOverlay — open-ended reflection overlay.
 *
 * Pauses the video and prompts the learner to write a free-text
 * response to a reflection prompt before continuing.
 *
 * Props:
 *   question   — string prompt from the marker's `question` field
 *   markerId   — string marker ID for event context
 *   sceneId    — string scene ID for event context
 *   domain     — string domain for event context
 *   onDismiss  — () => void  called after save or skip
 */

import React, { useRef, useState } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

export default function PauseAndReflectOverlay({ question, markerId, sceneId, domain, onDismiss }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const mountTimeRef = useRef(Date.now());

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'pause_and_reflect',
      marker_id: markerId,
      response_text: text.trim(),
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });
    if (onDismiss) onDismiss();
  };

  const handleSkip = async () => {
    await emitLearnerEvent({
      event_type: 'overlay_skipped',
      overlay_type: 'pause_and_reflect',
      marker_id: markerId,
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });
    if (onDismiss) onDismiss();
  };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={labelStyle}>Pause & Reflect</span>
          <button onClick={handleSkip} style={skipBtnStyle} title="Skip this reflection">
            Skip
          </button>
        </div>

        <p style={promptStyle}>{question || 'Take a moment to reflect on what you just learned.'}</p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your response here…"
          rows={5}
          style={textareaStyle}
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...saveBtnStyle, opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const cardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 560,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const skipBtnStyle = {
  background: 'none',
  border: '1px solid #334155',
  borderRadius: 4,
  color: '#64748b',
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
};

const promptStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 16px',
  lineHeight: 1.5,
};

const textareaStyle = {
  width: '100%',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 8,
  color: '#e2e8f0',
  fontSize: 14,
  padding: '10px 12px',
  resize: 'vertical',
  outline: 'none',
  lineHeight: 1.6,
  boxSizing: 'border-box',
};

const saveBtnStyle = {
  flex: 1,
  padding: '10px 0',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
};
